import clsx from "clsx";
import { makeStyles } from "@material-ui/core/";

export type StatThresholds = {
  high?: number | null;
  low?: number | null;
  warning?: number | null;
};
const getStatus = (
  value: number | null,
  type: string,
  { high = null, warning = null, low = null }: StatThresholds
) => {
  if (value === null) {
    return "grey";
  }
  if (high === null && low === null) {
    return null;
  }
  const isHigh = high !== null && value >= high;
  const isWarning =
    warning !== null && value > (low ?? -Infinity) && value <= warning;
  const isLow = low !== null && value <= low;
  if (type === "mildew") {
    return isHigh ? "danger" : isLow ? "primary" : "warning";
  }
  if (type === "moisture_content") {
    return isLow
      ? "danger"
      : isWarning
      ? "warning"
      : isHigh
      ? "info"
      : "primary";
  }
  if (type === "gains") {
    return isHigh ? "info" : isLow ? "danger" : "primary";
  }
  return isHigh ? "danger" : isLow ? "pink" : "primary";
};

type StatParameters = Record<string, { value: number }>;

const getThreshold = (
  type: string,
  defaults: StatThresholds,
  params: StatParameters | null = null
) => {
  if (type !== "moisture_content") return defaults;
  if (params === null) return defaults;

  return Object.keys(params).reduce(
    (obj: StatThresholds, item) => {
      // full_point (2) or field_capactiy (1)
      if (["2", "1"].includes(item)) {
        obj.high = params[item].value && Number(params[item].value);
      }
      // med_squeeze (4) or irri_trigger (8)
      if (["4", "8"].includes(item)) {
        obj.warning = params[item].value && Number(params[item].value);
      }
      // irri_lower (5) or refill_point (9)
      if (["5", "9"].includes(item)) {
        obj.low = params[item].value && Number(params[item].value);
      }
      return obj;
    },
    { high: null, low: null }
  );
};

const Statistic = ({
  type,
  value,
  suffix,
  thresholds,
  parameters,
}: {
  type: string;
  value: number | null;
  suffix?: string;
  thresholds: StatThresholds;
  parameters: StatParameters | null;
}) => {
  const useStyles = makeStyles((theme) => ({
    stat: { fontWeight: "bold" },
    primary: {
      color: theme.palette.primary.main,
    },
    warning: {
      color: theme.palette.warning.main,
    },
    info: {
      color: theme.palette.info.main,
    },
    danger: {
      // @ts-expect-error danger not in palette type
      color: theme.palette.danger.main,
    },
    pink: {
      // @ts-expect-error danger not in palette type
      color: theme.palette.pink.main,
    },
    grey: {
      color: theme.palette.grey[400],
    },
  }));
  const classes = useStyles();
  const status = getStatus(
    value,
    type,
    getThreshold(type, thresholds, parameters)
  );

  return (
    <span className={clsx(classes.stat, status && classes[status])}>
      {value === null ? "N/A" : suffix ? value + suffix : value}
    </span>
  );
};

export default Statistic;
